import { LoadingIndicator } from 'components/LoadingIndicator';
import { Page } from 'components/Page/Page';
import { StickableBootstrapTable } from 'components/Table/StickableBootstrapTable';
import i18n from 'i18n';
import { ECSegmentListColumn, ecSegmentStatusOptions, useECSegmentListModel } from './ECSegmentListModel';
import { useCallback, useMemo } from 'react';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { notRoles } from 'core/permission/PermissionDSL';
import { RoleNames } from 'core';
import { SessionStorageItemKeys } from 'helper/StorageHelper';
import { RecentGroupDropdown } from 'components/RecentGroupDropdown/RecentGroupDropdown';
import { SelectOptions } from 'components/commonType';
import styles from './ecSegmentList.module.scss';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { ECSegmentListDTO, ECSegmentStatus, ECSegmentType, FINAL_STATUS } from 'core/ecSegment/ECSegment';
import { faPencilAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import { DeleteECSegmentModal } from './DeleteSegmentModal';
import { IntersectECSegmentModal } from './IntersectECSegmentModal';
import { uniq } from 'lodash';
import Select from 'components/Select/Select';
const cx = classNames.bind(styles);

export const ECSegmentList: React.FC<{
  advertiserOptions: SelectOptions[]
}> = ({
  advertiserOptions
}) => {

  const {
    page,
    loading,
    columns,
    ecStatus,
    ecSegments,
    sizePerPage,
    searchString,
    advertiserId,
    canAddSegment,
    batchOperations,
    filteredECSegments,
    selectedECSegments,
    ecSegemntsToDelete,
    ecSegmentsToIntersect,
    setPage,
    setECStatus,
    onHandleSearch,
    onHandleSelect,
    deleteECSegment,
    setAdvertiserId,
    onRemoveAllSelect,
    onHandleSelectPage,
    onCloseDeleteECSegmentModal,
    onCloseIntersectECSegmentModal
  } = useECSegmentListModel();

  const renderFilterArea = useCallback(() => {
    return (
      <div className={styles.filterArea}>
        <PermissionChecker permissionAware={notRoles(RoleNames.adsAdmin, RoleNames.adsReport, RoleNames.adsSales)}>
          <div className={styles.filterList}>
            <div className={styles.title}>
              {i18n.t<string>('ecSegmentList.labels.advertiserListTitle')}
            </div>
            <RecentGroupDropdown
              value={advertiserId}
              options={advertiserOptions}
              storageKey={SessionStorageItemKeys.ADVERTISER}
              callback={setAdvertiserId}
            />
          </div>
        </PermissionChecker>
        <div className={styles.filterList}>
          <div className={styles.title}>
            {i18n.t<string>('ecSegmentList.labels.statusFilterTitle')}
          </div>
          <Select
            className={styles.statusSelect}
            simpleValue
            value={ecStatus}
            options={ecSegmentStatusOptions}
            onChange={setECStatus}
          />
        </div>
      </div>
    );
  }, [advertiserOptions, advertiserId, ecStatus, setECStatus, setAdvertiserId]);

  const renderRowBtns = useCallback((ecSegment: ECSegmentListDTO) => {
    const canEdit = !FINAL_STATUS.includes(ecSegment.status);
    const isIntersectionSegment = ecSegment.type === ECSegmentType.INTERSECT;
    const canDelete = ecSegment.status !== ECSegmentStatus.ACTIVE;
    const onDelete = () => {
      deleteECSegment(ecSegment.id);
    };
    return [
      <IconWithTooltip
        key={`edit-btn-${ecSegment.id}`}
        disabled={!canEdit || isIntersectionSegment}
        icon={faPencilAlt}
        tooltipProps={{
          id: `edit-ec-segments-${ecSegment.id}`,
          link: `/ec-segments/${ecSegment.id}/edit`,
          tooltip: isIntersectionSegment
            ? i18n.t<string>('ecSegmentList.hints.cannotEditIntersectionSegment')
            : canEdit
            ? i18n.t<string>('ecSegmentList.hints.edit')
            : i18n.t<string>('ecSegmentList.hints.cannotEdit')
        }}
      />,
      <IconWithTooltip
        key={`delete-btn-${ecSegment.id}`}
        disabled={!canDelete}
        icon={faTrashAlt}
        onClick={onDelete}
        tooltipProps={{
          id: `delete-ec-segments-${ecSegment.id}`,
          tooltip: canDelete
            ? i18n.t<string>('ecSegmentList.hints.delete')
            : i18n.t<string>('ecSegmentList.hints.cannotDelete')
        }}
      />
    ];
  }, [deleteECSegment]);

  const renderListOperations = useCallback(() => {
    return [
      <div
        className={cx(styles.newBtn, {
          [styles.disabled]: !canAddSegment
        })}
        key='new-btn'
      >
        {!canAddSegment && (
          <span className={styles.tooltip}>
            {i18n.t<string>('ecSegmentList.hints.cannotCreate')}
          </span>
        )}
        <Link to={'/ec-segments/new'} className={'btn btn-tertiary btn-sm'}>
          {i18n.t<string>('ecSegmentList.buttons.create')}
        </Link>
      </div>
    ];
  }, [canAddSegment]);

  const intersectECSegmentsAdvertiserId = useMemo(() => {
    if (!ecSegmentsToIntersect) {
      return undefined;
    }
    const advertiserIds = ecSegments
      .filter((ecSegment) => ecSegmentsToIntersect.includes(ecSegment.id))
      .map((ecSegment) => ecSegment.advertiserId);
    return uniq(advertiserIds).length === 1 ? advertiserIds[0] : undefined;
  }, [ecSegments, ecSegmentsToIntersect]);

  return (
    <Page
      title={i18n.t<string>('ecSegmentList.title')}
      renderInTopArea={renderFilterArea}
    >
      {loading && <LoadingIndicator />}
      {ecSegemntsToDelete && (
        <DeleteECSegmentModal
          selectedSegmentIds={ecSegemntsToDelete}
          onClose={onCloseDeleteECSegmentModal}
        />
      )}
      {ecSegmentsToIntersect && intersectECSegmentsAdvertiserId && (
        <IntersectECSegmentModal
          advertiserId={intersectECSegmentsAdvertiserId}
          selectedSegmentIds={[ecSegmentsToIntersect[0], ecSegmentsToIntersect[1]]}
          onClose={onCloseIntersectECSegmentModal}
        />
      )}
      <StickableBootstrapTable
        keyField={ECSegmentListColumn.ID}
        customPadding={false}
        data={filteredECSegments}
        columns={columns}
        noDataIndication={i18n.t<string>('ecSegmentList.labels.noData')}
        renderRowBtns={renderRowBtns}
        renderListOperations={renderListOperations}
        defaultSearchString={searchString}
        onHandleSearch={onHandleSearch}
        searchbarPlaceholder={i18n.t<string>('ecSegmentList.placeholders.search')}
        batchOperaionConfig={batchOperations}
        selectedRows={selectedECSegments}
        onSelect={onHandleSelect}
        onSelectPage={onHandleSelectPage}
        onRemoveAllSelect={onRemoveAllSelect}
        pagination={{
          page,
          sizePerPage,
          onPageChange: setPage
        }}
      />
    </Page>
  );
};
